<script setup>
import { onMounted, computed, watch, ref } from "vue";
import { useRouter } from "vue-router";

import Filters from "@/components/livelihood/filters/questionnaire/Filters.vue";

import debounce from "lodash/debounce";
import moment from "moment";
import { useLivelihoodQuestionnaireStore } from "@/stores";

const store = useLivelihoodQuestionnaireStore();

const router = useRouter();

const data = computed(() => store.overview);

const params = computed(() => store.requestQueryParams);

const pagination = computed(() => store.pagination);

const isFetching = ref(false);

const download = ref(false);

const exportTable = async () => {
  download.value = true;
  const resp = await store.downloadExcel(params.value);

  const url = window.URL.createObjectURL(resp.data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "livelihood_questionnaires.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  download.value = false;
};

const getLivelihoods = async () => {
  isFetching.value = true;

  params.value.set("page", pagination.value.currentPage - 1);
  params.value.set("size", pagination.value.pageSize);

  await store.getLivelhoodOverview(params.value);

  isFetching.value = false;
};

const checkClickEvent = (event) => {
  const check = event.target && event.target.classList.contains("mdi-pencil");
  return check;
};

const edit = (item, event) => {
  const check = checkClickEvent(event);
  if (check) {
    router.push({
      path: `/livelihood/questionnaire/edit/${item}`,
    });
  }
};

const formatDate = (date) => {
  if (date === null || date === undefined) return null;
  return moment(date, "YYYY-MM-DD").format("DD-MM-YYYY");
};

const livelihoodsDebounced = debounce(getLivelihoods, 500);

watch(
  pagination,
  () => {
    if (!isFetching.value) {
      getLivelihoods();
    }
  },
  {
    deep: true,
  },
);

watch(
  params,
  () => {
    livelihoodsDebounced();
  },
  {
    deep: true,
  },
);

const headers = [
  {
    title: "Actions",
    key: "actions",
    align: "start",
    width: "50px",
    sortable: false,
    fixed: true,
  },
  { title: "Name", key: "name" },
  { title: "Date", key: "date" },
  { title: "Beneficiary", key: "beneficiary" },
  { title: "Employability Officer", key: "employabilityOfficerName" },
  { title: "Talent Acquisition Officer", key: "talentAcquisitionOfficerName" },
  { title: "Branch", key: "branch" },
  { title: "Projects", key: "projects" },
  { title: "Courses", key: "courses" },
  { title: "Completed Training", key: "completedCourses" },
  { title: "How did you find the job?", key: "jobFoundMethod" },
  { title: "Did Odyssea support?", key: "didOdysseaSupport" },
  { title: "Net salary range", key: "netSalaryRange" },
  { title: "Is part of network?", key: "partOfNetwork" },
  { title: "Company/Employer", key: "employerName" },
  { title: "Job Title/Position", key: "jobTitle" },
  { title: "Type of Employment", key: "typeOfEmployment" },
  { title: "Job started", key: "jobStartDate" },
  { title: "Job ended", key: "jobEndDate" },
  { title: "Social security", key: "socialSecurity" },
  { title: "Recommend employer", key: "recommendEmployer" },
  { title: "Recommend employer reasoning", key: "recommendEmployerReasoning" },
  { title: "Job satisfaction level", key: "jobSatisfactionLevel" },
  { title: "Helps grow", key: "helpsGrow" },
  { title: "Job matches skills", key: "jobMatchSkills" },
  { title: "Feel secure in job", key: "feelSecureInJob" },
  { title: "Get along", key: "getAlong" },
  { title: "Still looking for job", key: "stillLookingForJob" },
  { title: "Looking for ", key: "newJobLookingFor" },
  { title: "Other", key: "otherJobPriority" },
];

// onBeforeMount(() => {
//   livelihoodStore.resetAction();
// });

onMounted(() => {
  getLivelihoods();
});
</script>

<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-10 page-title">Livelihood Overview</h1>
        <Filters variant="livelihood" />
        <div class="d-flex justify-space-between align-center mb-5">
          <h3>Total: {{ pagination.totalItems }}</h3>
          <v-btn
            color="employability"
            :loading="download"
            :disabled="download"
            @click="exportTable()"
          >
            <div class="d-flex align-center">
              <v-icon>mdi-file-excel</v-icon>
              <span>Export</span>
            </div>
          </v-btn>
        </div>
        <v-data-table
          :items="data"
          :headers="headers"
          :items-per-page="pagination.pageSize"
          :hide-default-footer="true"
          :loading="isFetching"
          class="livelihood-table bordered-columns"
        >
          <template #item.date="{ item }">
            <span style="white-space: nowrap">{{ formatDate(item.date) }}</span>
          </template>

          <template #item.beneficiary="{ item }">
            <div
              class="d-flex flex-column justify-space-between justify-center mt-1"
              style="gap: 0px"
            >
              <router-link
                v-if="item.beneficiaryIntake || item.beneficiary"
                :to="'/beneficiary/intake/view/' + item.beneficiaryId"
                target="_blank"
                class="chip-link-color-employability"
              >
                <v-chip> {{ item.beneficiaryIntake?.name ?? item.beneficiary?.name }} </v-chip>
              </router-link>
              <span v-else>-</span>
            </div>
          </template>

          <template #item.projects="{ item }">
            <div class="pt-1 pb-1 d-flex flex-column gap-10">
              <div v-for="project in item.projects" :key="project.id">
                <v-chip>{{ project.name }}</v-chip>
              </div>
              <span v-if="!item.projects || item.projects.length === 0">-</span>
            </div>
          </template>

          <template #item.courses="{ item }">
            <div class="pt-1 pb-1 d-flex flex-column gap-10">
              <div v-for="course in item.courses" :key="course.id">
                <router-link
                  :to="'/academy/view/' + course.id"
                  target="_blank"
                  class="chip-link-color-academy"
                >
                  <v-chip> {{ course.cycle }} </v-chip>
                </router-link>
              </div>
              <span v-if="!item.courses || item.courses.length === 0">-</span>
            </div>
          </template>

          <template #item.completedCourses="{ item }">
            <div class="pt-1 pb-1 d-flex flex-column gap-10">
              <div v-for="course in item.completedCourses" :key="course.id">
                <router-link
                  :to="'/academy/view/' + course.id"
                  target="_blank"
                  class="chip-link-color-academy"
                >
                  <v-chip> {{ course.cycle }} </v-chip>
                </router-link>
              </div>
              <span
                v-if="
                  !item.completedCourses || item.completedCourses.length === 0
                "
                >-</span
              >
            </div>
          </template>

          <!-- Company/Employer Name (conditional based on partOfNetwork) -->
          <template #item.employerName="{ item }">
            <div>
              <!-- If partOfNetwork is YES, show company name -->
              <span v-if="item.partOfNetwork === 'Yes'">
                {{ item.companyName || "-" }}
              </span>
              <!-- Otherwise show employer name text -->
              <span v-else>
                {{ item.employerName || "-" }}
              </span>
            </div>
          </template>

          <!-- Job Title/Position (conditional based on partOfNetwork) -->
          <template #item.jobTitle="{ item }">
            <div>
              <!-- If partOfNetwork is YES and jobTitle object exists, show as link -->
              <template
                v-if="
                  item.partOfNetwork === 'Yes' &&
                  item.jobTitle &&
                  item.jobTitle.id
                "
              >
                <router-link
                  :to="'/job_position/view/' + item.jobTitle.id"
                  target="_blank"
                  class="chip-link-color-employability"
                >
                  <v-chip> {{ item.jobTitle.title }} </v-chip>
                </router-link>
              </template>
              <!-- Otherwise show job title text -->
              <template v-else>
                <span>{{ item.jobTitleText || "-" }}</span>
              </template>
            </div>
          </template>

          <template #item.netSalaryRange="{ item }">
            <div class="d-flex flex-column align-center">
              <span
                v-if="
                  Array.isArray(item.netSalaryRange) &&
                  item.netSalaryRange.length
                "
              >
                {{ Math.min(...item.netSalaryRange) }} -
                {{ Math.max(...item.netSalaryRange) }}
              </span>
              <span v-else class="text-disabled"></span>
            </div>
          </template>

          <template #item.newJobLookingFor="{ item }">
            <div>
              <span
                v-if="
                  Array.isArray(item.newJobLookingFor) &&
                  item.newJobLookingFor.length > 0
                "
              >
                {{ item.newJobLookingFor.join(", ") }}
              </span>
              <span v-else class="text-disabled"></span>
            </div>
          </template>

          <template #item.jobFoundMethod="{ item }">
            <div>
              {{ item.jobFoundMethod?.name || "-" }}
            </div>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex ga-2 justify-center">
              <v-btn
                icon="mdi-pencil"
                size="x-small"
                color="employability"
                @click="edit(item.id, $event)"
              />
            </div>
          </template>
        </v-data-table>
        <div class="mt-5">
          <v-pagination
            v-model="pagination.currentPage"
            :length="pagination.pageCount"
            :total-visible="5"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-employability));
}
.v-table {
  table-layout: fixed;

  td,
  th {
    border-right: thin solid
      rgba(var(--v-border-color), var(--v-border-opacity));
    white-space: normal;
  }
}
</style>
